import channel from '../../../socket'
import i18n from '../../../i18n'

const SELECTORS = Object.freeze({
  newPage: '.js-post_imports_new_page',
  sourceFile: 'input[type="file"][name="post_import_form[source_file]"]',
  continueButton: '.js-post_imports_continue_button',
  root: '.js-post_imports_show',
  selectAll: '.js-post_imports_select_all',
  itemSelect: '.js-post_imports_item_select',
  selectedCount: '.js-selected-num',
  settingsToggle: '.js-post_imports_toggle_settings',
  settingsRow: '.js-post_imports_settings_row',
  itemSettingsFlag: '.js-post_imports_has_item_settings',
  rowStatus: '.js-post_imports_row_status',
  rowStatusBadge: '.js-post_imports_row_status_badge',
  progressBadge: '.js-post_imports_progress_badge',
  previewLink: '.js-post_imports_preview_link',
  previewLinkLabel: '.js-post_imports_preview_link_label',
  editorPreviewLink: '.js-post_imports_editor_preview_link',
  editorPreviewLinkLabel: '.js-post_imports_editor_preview_link_label'
})

const SOCKET_EVENTS = Object.freeze({
  POST_IMPORT_JOB_DONE: 'post_import_job_done',
  POST_IMPORT_COMPLETE: 'post_import_complete'
})

const DEFAULT_BADGE_VARIANT = 'green'

const STATUS_DISPLAY = Object.freeze({
  completed: { label: i18n.$gettext('Imported'), variant: DEFAULT_BADGE_VARIANT },
  failed: { label: i18n.$gettext('Failed'), variant: 'orange' },
  pending: { label: i18n.$gettext('Importing'), variant: 'orange' },
  skipped: { label: i18n.$gettext('Skipped'), variant: 'gray' },
  ready: { label: i18n.$gettext('Ready'), variant: 'gray' }
})

const progressLabel = (done, total, failed) => {
  if (failed > 0) {
    return i18n.$gettext('%{done}/%{total} done (%{failed} failed)', {
      done,
      total,
      failed
    })
  }

  return i18n.$gettext('%{done}/%{total} done', {
    done,
    total
  })
}

const setBadgeDisplay = (badge, { label, variant }) => {
  badge.textContent = label

  Array.from(badge.classList).forEach(className => {
    if (className.startsWith('badge--')) {
      badge.classList.remove(className)
    }
  })

  if (variant !== DEFAULT_BADGE_VARIANT) {
    badge.classList.add(`badge--${variant}`)
  }
}

const setStatusBadge = (rowStatus, status) => {
  rowStatus.dataset.status = status

  const statusBadge = rowStatus.querySelector(SELECTORS.rowStatusBadge)
  if (statusBadge) {
    setBadgeDisplay(statusBadge, STATUS_DISPLAY[status] || STATUS_DISPLAY.ready)
  }
}

const updateProgressCounter = container => {
  const progressBadge = container.querySelector(SELECTORS.progressBadge)
  if (!progressBadge) {
    return
  }

  const statusBadges = Array.from(container.querySelectorAll(SELECTORS.rowStatus))
  const total = statusBadges.length
  const completed = statusBadges.filter(badge => badge.dataset.status === 'completed').length
  const failed = statusBadges.filter(badge => badge.dataset.status === 'failed').length
  const done = completed + failed

  setBadgeDisplay(progressBadge, {
    label: progressLabel(done, total, failed),
    variant: failed > 0 ? 'orange' : 'gray'
  })
}

const updateJobStatus = (container, guid, status) => {
  const rowStatus = container.querySelector(`${SELECTORS.rowStatus}[data-guid="${guid}"]`)
  if (!rowStatus) {
    return
  }

  setStatusBadge(rowStatus, status)
  updateProgressCounter(container)
}

const setLinkLabel = (link, label, labelSelector) => {
  const labelElement = link.querySelector(labelSelector)

  if (labelElement) {
    labelElement.textContent = label
  } else {
    link.textContent = label
  }
}

const updateCompletedItemLinks = (container, guid, postId) => {
  if (!postId || !container.dataset.viewPostPathPrefix || !container.dataset.editPostPathPrefix) {
    return
  }

  const rowStatus = container.querySelector(`${SELECTORS.rowStatus}[data-guid="${guid}"]`)
  const row = rowStatus?.closest('tr')

  if (!row) {
    return
  }

  const previewLink = row.querySelector(SELECTORS.previewLink)
  if (previewLink) {
    previewLink.href = `${container.dataset.viewPostPathPrefix}/${postId}`
    setLinkLabel(previewLink, container.dataset.viewPostLabel || 'View', SELECTORS.previewLinkLabel)
  }

  const editorLink = row.querySelector(SELECTORS.editorPreviewLink)
  if (editorLink) {
    editorLink.href = `${container.dataset.editPostPathPrefix}/${postId}/edit`
    setLinkLabel(editorLink, container.dataset.editPostLabel || 'Edit', SELECTORS.editorPreviewLinkLabel)
  }
}

const onJobDone = (container, payload) => {
  if (payload.import_id !== container.dataset.postImportId) {
    return
  }

  updateJobStatus(container, payload.guid, payload.status)
  updateCompletedItemLinks(container, payload.guid, payload.post_id)
}

const onImportComplete = async (container, payload) => {
  if (payload.import_id !== container.dataset.postImportId) {
    return
  }

  if (window.up?.reload) {
    await window.up.reload('.unpoly_wrapper', { scroll: false })
  } else {
    window.location.reload()
  }
}

const markItemSettingsPresent = settingsRow => {
  const hasItemSettingsInput = settingsRow.querySelector(SELECTORS.itemSettingsFlag)

  if (hasItemSettingsInput) {
    hasItemSettingsInput.value = 'true'
  }
}

const bindSelectionControls = container => {
  const selectAllCheckbox = container.querySelector(SELECTORS.selectAll)
  const itemCheckboxes = Array.from(container.querySelectorAll(SELECTORS.itemSelect))
  const selectedCountElement = container.querySelector(SELECTORS.selectedCount)

  const updateSelectedCount = () => {
    const selectedCount = itemCheckboxes.filter(checkbox => checkbox.checked).length

    if (selectedCountElement) {
      selectedCountElement.textContent = String(selectedCount)
    }

    if (selectAllCheckbox) {
      selectAllCheckbox.checked = selectedCount > 0 && selectedCount === itemCheckboxes.length
      selectAllCheckbox.indeterminate = selectedCount > 0 && selectedCount < itemCheckboxes.length
    }
  }

  const onSelectAllChange = () => {
    itemCheckboxes.forEach(checkbox => {
      checkbox.checked = selectAllCheckbox.checked
    })

    updateSelectedCount()
  }

  selectAllCheckbox?.addEventListener('change', onSelectAllChange)
  itemCheckboxes.forEach(checkbox => checkbox.addEventListener('change', updateSelectedCount))
  updateSelectedCount()

  return () => {
    selectAllCheckbox?.removeEventListener('change', onSelectAllChange)
    itemCheckboxes.forEach(checkbox => checkbox.removeEventListener('change', updateSelectedCount))
  }
}

const bindSettingsRows = container => {
  const settingsToggles = Array.from(container.querySelectorAll(SELECTORS.settingsToggle))

  const settingsToggleHandlers = settingsToggles.map(button => {
    const handler = () => {
      const targetId = button.dataset.target

      if (!targetId) {
        return
      }

      const settingsRow = container.querySelector(`#${targetId}`)

      if (!settingsRow) {
        return
      }

      settingsRow.hidden = !settingsRow.hidden
      button.setAttribute('aria-expanded', settingsRow.hidden ? 'false' : 'true')

      if (!settingsRow.hidden) {
        markItemSettingsPresent(settingsRow)
      }
    }

    button.addEventListener('click', handler)
    return { button, handler }
  })

  const onContainerChange = event => {
    const target = event.target

    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) {
      return
    }

    const settingsRow = target.closest(SELECTORS.settingsRow)

    if (!settingsRow) {
      return
    }

    markItemSettingsPresent(settingsRow)
  }

  container.addEventListener('change', onContainerChange)

  return () => {
    settingsToggleHandlers.forEach(({ button, handler }) => {
      button.removeEventListener('click', handler)
    })

    container.removeEventListener('change', onContainerChange)
  }
}

const bindProgressEvents = container => {
  let jobDoneHandler
  let completeHandler

  if (channel && container.dataset.postImportId) {
    jobDoneHandler = channel.on(SOCKET_EVENTS.POST_IMPORT_JOB_DONE, payload => onJobDone(container, payload))
    completeHandler = channel.on(SOCKET_EVENTS.POST_IMPORT_COMPLETE, payload => onImportComplete(container, payload))
  }

  updateProgressCounter(container)

  return () => {
    if (channel && jobDoneHandler) {
      channel.off(SOCKET_EVENTS.POST_IMPORT_JOB_DONE, jobDoneHandler)
    }

    if (channel && completeHandler) {
      channel.off(SOCKET_EVENTS.POST_IMPORT_COMPLETE, completeHandler)
    }
  }
}

const updateNewPageContinueButton = container => {
  const sourceFile = container.querySelector(SELECTORS.sourceFile)
  const continueButton = container.querySelector(SELECTORS.continueButton)

  if (continueButton) {
    continueButton.disabled = !(sourceFile?.files?.length > 0)
  }
}

up.compiler(SELECTORS.newPage, container => {
  const sourceFile = container.querySelector(SELECTORS.sourceFile)
  const onChange = () => updateNewPageContinueButton(container)

  sourceFile?.addEventListener('change', onChange)
  updateNewPageContinueButton(container)

  return () => {
    sourceFile?.removeEventListener('change', onChange)
  }
})

up.compiler(SELECTORS.root, container => {
  const cleanupSelectionControls = bindSelectionControls(container)
  const cleanupSettingsRows = bindSettingsRows(container)
  const cleanupProgressEvents = bindProgressEvents(container)

  return () => {
    cleanupSelectionControls()
    cleanupSettingsRows()
    cleanupProgressEvents()
  }
})
